import React, { Fragment, useEffect, useState } from 'react';
import FileSaver from 'file-saver';

import FileUpload from 'b2b-ui-components-lib/lib/components/fileUpload';

import { getFromSessionStorage } from 'common/utils/app.utils';

import { deleteDocumentsForSummary, getUploadedDocumentsForSummary, uploadDocumentsForSummary } from 'home/view/manage-feasibility/services';

import { MULTI_SITE_MODULE_ID, ERROR_TOAST, DEFAULT_TOAST_MSG, SUMMARY_AUTH_TOKEN } from 'home/view/manage-feasibility/constants';

import './index.scss';

export const keyForGSTFiles = {
  GST_CERTIFICATE: 'filesForGSTCerificate',
  GST_LOA: 'filesForGSTLOA'
};

const defaultFileUploadProps = {
  name: 'multi-site-doc-upload',
  label: 'Click or drop file to upload',
  placeholder: '',
  helpTexts: ['Supported file format is .PDF, .PNG, .JPG • File size must be maximum 1Mb'],
  accept: 'application/pdf, image/jpeg, image/jpg, image/png',
  required: true
};

const DocsUploadComponent = ({
  label = '',
  multiple = false,
  fileSchema = {},
  type = '',
  lineItemId = '',
  onUploadSuccessCb = () => {},
  setToast = () => {},
  defaultFiles = [],
  isOrderConfirmation = false
}) => {
  const [uploadFileProps, setUploadFileProps] = useState(defaultFileUploadProps);

  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [progressValue, setProgressValue] = useState(0);
  const summaryAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);
  const [uploadedLoaKeys, setUploadedLoaKeys] = useState([]);

  useEffect(() => {
    onUploadSuccessCb(isFileUploaded, uploadedFiles);
  }, [isFileUploaded, uploadedFiles]);

  useEffect(() => {
    if (defaultFiles && defaultFiles.length > 0) {
      const uploadedKeys = [];

      Array.from(defaultFiles).forEach((file, index) => {
        const { name } = (Array.isArray(file) && file[0]) || file;

        let fileNameParts = name && name.includes('@') && name.split('@');
        if (fileNameParts && fileNameParts.length > 0) {
          const key = name && name.split('@')[1] && name.split('@')[1] - 1;
          uploadedKeys.push({
            fileName: name,
            key
          });
        } else {
          uploadedKeys.push({
            fileName: name,
            key: index
          });
        }
      });

      setIsFileUploaded(false);
      setUploadedFiles(defaultFiles);
      setUploadFileProps((uploadFileProps) => ({
        ...uploadFileProps,
        helpTexts: [],
        errors: [],
        renderUploadedFiles: displayUploadedFiles(Array.from(defaultFiles), fileSchema, lineItemId, uploadedKeys, isOrderConfirmation),
        key: Date.now()
      }));

      setUploadedLoaKeys(uploadedKeys);
    }
  }, []);

  const validateFileAndGetFormData = ({ files, supportedTypes, maxFileLimit, documentNames, documentType, lineItemId }) => {
    let isFilesValid = true;
    let errorMessage = '';
    const formData = new FormData();
    const fileList = Array.from(files);
    let uploadedIndexes = [...uploadedLoaKeys];
    fileList.forEach((file, index) => {
      const { name, type, size } = Array.isArray(file) ? file[0] : file;

      if (fileList.length > documentNames.length && type !== 'application/pdf') {
        errorMessage = `Maximum ${documentNames.length} files are allowed in multiple upload`;
        isFilesValid = false;
      }

      if (!(Array.isArray(supportedTypes) && supportedTypes.includes(type))) {
        errorMessage = `Invalid file format. Try to re-upload the correct format`;
        isFilesValid = false;
      } else if (type === 'application/pdf' && fileList.length > 1) {
        errorMessage = 'Only single upload is allowed in case of pdf file';
        isFilesValid = false;
      } else if (!(size <= maxFileLimit)) {
        errorMessage = `Maximum file size is ${maxFileLimit / 1024 / 1024} Mb`;
        isFilesValid = false;
      }

      const fileIndex = getMissingLoaIndex(uploadedIndexes);
      uploadedIndexes.push({
        fileName: name,
        key: fileIndex
      });
      formData.append(`documents['${documentNames[fileIndex]}']`, Array.isArray(file) ? file[0] : file);
    });
    formData.append('documentType', documentType);
    formData.append('lineItemId', lineItemId);

    return { isFilesValid, formData, errorMessage, uploadedIndexes };
  };

  const getMissingLoaIndex = (arr) => {
    const baseList = [0, 1, 2];
    const indexList = arr.map((item) => item.key);
    let missingIndex = 0;
    for (let i = 0; i < baseList.length; i++) {
      if (!indexList.includes(baseList[i])) {
        missingIndex = baseList[i];
        break;
      }
    }

    return missingIndex;
  };

  const onUploadProgress = (progressEvent) => {
    const loadingPercentage = (progressEvent.loaded / progressEvent.total) * 100;
    setProgressValue(loadingPercentage - 5);
  };

  const onUploadChange = async (files, lineItemId, fileSchema) => {
    if (!files) {
      setUploadFileProps({
        ...uploadFileProps,
        value: [],
        key: Date.now()
      });

      setIsFileUploaded(false);
      setUploadedFiles([]);
      return false;
    }

    const { isFilesValid, formData, errorMessage, uploadedIndexes } = validateFileAndGetFormData({ files, lineItemId, ...fileSchema });

    if (isFilesValid) {
      try {
        const { res, err } = await uploadDocumentsForSummary(formData, summaryAuthToken, onUploadProgress);

        if (res) {
          const { data: { success = false } = {} } = res;

          if (success) {
            const updatedFileList = Array.from(files);

            setProgressValue(100);
            setTimeout(() => setProgressValue(0));
            setIsFileUploaded(true);
            setUploadedLoaKeys(uploadedIndexes);
            setUploadedFiles((uploadedFiles) => [...uploadedFiles, ...updatedFileList]);
            setUploadFileProps((uploadFileProps) => ({
              ...uploadFileProps,
              helpTexts: [],
              errors: [],
              renderUploadedFiles: displayUploadedFiles(Array.from([...uploadedFiles, ...updatedFileList]), fileSchema, lineItemId, uploadedIndexes, isOrderConfirmation),
              key: Date.now()
            }));
          }
        } else if (err) {
          const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

          setTimeout(() => setProgressValue(0));
          setIsFileUploaded(false);

          setToast(true, errorMsg, ERROR_TOAST);
          throw new Error(errorMsg);
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setToast(true, errorMessage, ERROR_TOAST);
    }
  };

  const getUploadedFile = async (documentName, documentType, type, fileName, files, lineItemId, fileSchema) => {
    const payload = { documentType, documentName, lineItemId };
    try {
      const { res, err } = await getUploadedDocumentsForSummary(payload, summaryAuthToken);

      if (res) {
        const { data } = res;

        const file = new Blob([data], { type });
        FileSaver.saveAs(file, fileName);
      } else if (err) {
        const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

        setIsFileUploaded(false);
        setUploadedFiles(files);

        setToast(true, errorMsg, ERROR_TOAST);
        setUploadFileProps((uploadFileProps) => ({
          ...uploadFileProps,
          errors: [],
          renderUploadedFiles: displayUploadedFiles(files, fileSchema, lineItemId, isOrderConfirmation),
          key: Date.now()
        }));
        throw new Error(errorMsg);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const displayUploadedFiles = (files, fileSchema, lineItemId, uploadedKeys = uploadedLoaKeys, isOrderConfirmation) => {
    if (Array.isArray(files) && files.length > 0) {
      return files.map((file, index) => {
        const { name, size, type } = (Array.isArray(file) && file[0]) || file;

        let fileName = name;
        let loa_key = index;

        let fileNameParts = name && name.includes('@') && name.split('@');
        if (fileNameParts && fileNameParts.length > 0) {
          fileName = name && name.split('@')[0];
        }

        const fileItem = uploadedKeys.find((item) => item.fileName === name);
        loa_key = fileItem && fileItem.key;

        return (
          <div
            key={loa_key + fileName}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span
              className="fileName"
              title={fileName}
              onClick={getUploadedFile.bind(null, fileSchema.documentNames[loa_key], fileSchema.documentType, type, fileName, files, lineItemId, fileSchema)}
            >
              {fileName}
            </span>
            {!isOrderConfirmation && (
              <span
                className="cross_icon"
                onClick={onDeleteUploadedFile.bind(null, files, fileName, fileSchema.documentNames[loa_key], fileSchema.documentType, lineItemId, fileSchema, uploadedKeys)}
                style={{ position: 'relative', top: 0 }}
              ></span>
            )}
          </div>
        );
      });
    }
  };

  const getFileName = (name) => {
    if (name && name.includes('@')) {
      const fileName = name.split('@') && name.split('@').length > 0 && name.split('@')[0];
      name = fileName;
    }

    return name;
  };

  const onDeleteUploadedFile = async (files, fileName, documentName, documentType, lineItemId, fileSchema, uploadedKeys = uploadedLoaKeys) => {
    const payload = lineItemId ? { documentType, documentName, lineItemId } : { documentType, documentName };
    try {
      const { res, err } = await deleteDocumentsForSummary(payload, summaryAuthToken);

      if (res) {
        removeFiles(fileName, files, fileSchema, lineItemId, uploadedKeys);
      } else if (err) {
        const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

        setIsFileUploaded(false);
        setUploadedFiles(files);

        setToast(true, errorMsg, ERROR_TOAST);
        setUploadFileProps((uploadFileProps) => ({
          ...uploadFileProps,
          errors: [],
          renderUploadedFiles: displayUploadedFiles(files, fileSchema, lineItemId, isOrderConfirmation),
          key: Date.now()
        }));
        throw new Error(errorMsg);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const removeFiles = (fileName, fileList = [], fileSchema, lineItemId, uploadedKeys = uploadedLoaKeys) => {
    const updatedFileList = fileList.filter((item) => {
      const { name } = Array.isArray(item) ? item[0] : item;
      return getFileName(name) !== fileName && item;
    });

    const updatedLoaKeys = uploadedKeys.filter((item) => getFileName(item.fileName) !== fileName) || [];

    if (updatedFileList.length === 0) {
      setUploadFileProps({
        ...uploadFileProps,
        errors: [],
        helpTexts: defaultFileUploadProps.helpTexts,
        renderUploadedFiles: displayUploadedFiles([], fileSchema, lineItemId, [], isOrderConfirmation),
        key: Date.now()
      });

      setIsFileUploaded(false);
      setUploadedFiles([]);
      setUploadedLoaKeys([]);
      return false;
    }

    setUploadedLoaKeys(updatedLoaKeys);
    setIsFileUploaded(false);
    setUploadedFiles(updatedFileList);
    setUploadFileProps((uploadFileProps) => ({
      ...uploadFileProps,
      renderUploadedFiles: displayUploadedFiles(updatedFileList, fileSchema, lineItemId, updatedLoaKeys, isOrderConfirmation),
      errors: [],
      key: Date.now()
    }));
  };

  return (
    <Fragment>
      <p className="fw-500"> {label}</p>
      <div className="loa_upload upload_document_area box-margin">
        <div className={`upload_container ${isOrderConfirmation ? 'upload_container_disabled' : ''}`}>
          <div className="progress_bar">
            <div
              className="progress_line"
              style={{
                width: `${progressValue}%`,
                background: '#cc000a',
                height: '4px',
                borderRadius: 10
              }}
            ></div>
          </div>
          <FileUpload optionalProps={uploadFileProps?.accept} onChange={(files) => onUploadChange(files, lineItemId, fileSchema)} {...uploadFileProps} multiple={multiple} />
          <div class="uploadedFiles">{uploadFileProps?.renderUploadedFiles}</div>
        </div>
      </div>
    </Fragment>
  );
};

export default DocsUploadComponent;
